import React, { useState, useEffect } from 'react';
import { Tag as TagIcon, Plus, RefreshCw, Search, Edit2, Trash2, RotateCcw, X } from 'lucide-react';
import { tagAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import Select from './Select';
import TablePager, { usePagedRows } from './TablePager';
import RowActionsMenu from './RowActionsMenu';
import useDialog from '../hooks/useDialog';

const TAG_TYPES = [
  { value: 'GENERAL',          label: 'General' },
  { value: 'AREA',             label: 'Location / Area' },
  { value: 'MEDICAL_CONDITION', label: 'Medical Condition' },
  { value: 'INSURANCE',        label: 'Insurance Type' },
  { value: 'VIP',              label: 'VIP Status' },
  { value: 'REFERRAL_SOURCE',  label: 'Referral Source' },
];

const TYPE_COLOR = {
  GENERAL:           'bg-gray-100 text-gray-700 border-gray-200',
  AREA:              'bg-blue-100 text-blue-700 border-blue-200',
  MEDICAL_CONDITION: 'bg-rose-100 text-rose-700 border-rose-200',
  INSURANCE:         'bg-indigo-100 text-indigo-700 border-indigo-200',
  VIP:               'bg-amber-100 text-amber-700 border-amber-200',
  REFERRAL_SOURCE:   'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const TagsManagement = () => {
  const notify = useToast();
  const confirm = useConfirm();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);   // tag being edited
  const [creating, setCreating] = useState(false); // create modal open

  const load = async () => {
    setLoading(true);
    try {
      const data = await tagAPI.getAllIncludingInactive();
      setTags(Array.isArray(data) ? data : []);
    } catch (e) {
      notify('error', 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = tags.filter(t => {
    if (!showInactive && !t.isActive) return false;
    if (search.trim() && !t.name.toLowerCase().includes(search.toLowerCase()) &&
        !(t.description || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const paging = usePagedRows(filtered, { id: 'tags', resetOn: [search, showInactive] });

  const handleDeactivate = async (tag) => {
    const usage = tag.patients?.length || 0;
    const usageMsg = usage > 0
      ? `\n\nThis tag is currently assigned to ${usage} patient${usage === 1 ? '' : 's'}. Existing assignments stay in place - the tag just stops being offered for new patients.`
      : '';
    const ok = await confirm({
      title: `Deactivate "${tag.name}"?`,
      message: `Users will no longer be able to assign this tag to new patients.${usageMsg}\n\nYou can reactivate it any time.`,
      confirmText: 'Deactivate',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await tagAPI.deactivate(tag.id);
      notify('success', `"${tag.name}" deactivated`);
      load();
    } catch (e) {
      notify('error', e.message || 'Failed to deactivate tag');
    }
  };

  const handleReactivate = async (tag) => {
    try {
      await tagAPI.reactivate(tag.id);
      notify('success', `"${tag.name}" reactivated`);
      load();
    } catch (e) {
      notify('error', e.message || 'Failed to reactivate tag');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
              <TagIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Tags</h1>
              <p className="text-gray-600">
                Admin-managed list. Only tags created here can be assigned to patients.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add Tag</span>
            </button>
            <button
              onClick={load}
              className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search tag name or description"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 focus:ring-emerald-500"
            />
            <span>Show inactive</span>
          </label>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {!loading && <TablePager paging={paging} noun="tag" filteredFrom={tags.length} />}
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
                <th className="px-6 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 hidden md:table-cell">Description</th>
                <th className="px-4 py-3 text-center">Patients</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="6"><div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="py-12 text-center text-gray-500">
                  {tags.length === 0 ? 'No tags yet. Click "Add Tag" to create one.' : 'No tags match your filters.'}
                </td></tr>
              ) : paging.pageRows.map(t => {
                const typeColor = TYPE_COLOR[t.tagType] || TYPE_COLOR.GENERAL;
                const typeLabel = TAG_TYPES.find(x => x.value === t.tagType)?.label || t.tagType;
                return (
                  <tr key={t.id} className={`hover:bg-gray-50/60 ${!t.isActive ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-3">
                      <div className="font-semibold text-gray-900">{t.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${typeColor}`}>
                        {typeLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{t.description || '—'}</td>
                    <td className="px-4 py-3 text-center text-sm">{t.patientCount ?? 0}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        t.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${t.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowActionsMenu items={[
                        { key: 'edit', label: 'Edit', icon: Edit2, iconClass: 'text-emerald-500', onClick: () => setEditing(t) },
                        t.isActive
                          ? { key: 'deact', label: 'Deactivate', icon: Trash2, danger: true, onClick: () => handleDeactivate(t) }
                          : { key: 'react', label: 'Reactivate', icon: RotateCcw, iconClass: 'text-emerald-500', onClick: () => handleReactivate(t) },
                      ]} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* The count repeated where a scrolled reader ends up, with the page buttons. */}
          {!loading && <TablePager paging={paging} noun="tag" filteredFrom={tags.length} position="bottom" />}
        </div>
      </div>

      {(creating || editing) && (
        <TagFormModal
          existing={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const TagFormModal = ({ existing, onClose, onSaved }) => {
  const notify = useToast();
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [tagType, setTagType] = useState(existing?.tagType || 'GENERAL');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const tagDialogRef = useDialog(() => { if (!saving) onClose(); });

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (name.trim().length < 2) { setError('Name must be at least 2 characters'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        tagType,
        isActive: existing?.isActive ?? true,
      };
      if (isEdit) await tagAPI.update(existing.id, payload);
      else        await tagAPI.create(payload);
      notify('success', isEdit ? 'Tag updated' : 'Tag created');
      onSaved();
    } catch (e) {
      setError(e.message || 'Failed to save tag');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={tagDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {/* No overflow-hidden on the shell — the Type dropdown inside needs to be able to extend past
          it. Corners are rounded on the header and footer instead. */}
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              {isEdit ? <Edit2 className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
            </div>
            <h2 className="text-lg font-bold text-white">{isEdit ? 'Edit Tag' : 'New Tag'}</h2>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Name <span className="text-red-500">*</span></label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={50}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g. VIP, Diabetic, Insurance"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Type</label>
            <Select
              value={tagType}
              onChange={setTagType}
              options={TAG_TYPES}
              buttonClassName="p-2.5 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={200}
              rows="2"
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
              placeholder="When to use this tag, who maintains it, etc."
            />
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create tag')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagsManagement;
